"use client";

import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Avatar } from "./ui/Avatar";
import { TimeAgo } from "./TimeAgo";
import { ReactionSummary } from "./ReactionSummary";
import { CommentThread } from "./CommentThread";
import { RichContent } from "./RichContent";
import { PostActionRow } from "./PostActionRow";
import type { RichTextInputHandle } from "./RichTextInput";
import { PostCommentForm } from "./PostCommentForm";
import { showPostTitle } from "@/lib/postTitle";
import type { Ready } from "@/lib/attachments";
import type { useAttachments } from "@/lib/hooks/useAttachments";
import { VideoPlayer } from "./VideoPlayer";
import type { Attachment, PostCard, ReactionSummary as Summary } from "@theta/api-client";
import type { ThreadComment } from "./CommentItem";
import type { CurrentUser } from "@/lib/hooks/useCurrentUser";
import { ESCAPE_LAYER, hasLayerInside, useEscapeLayer } from "@/lib/hooks/useEscapeLayer";
import { useFocusContainment } from "@/lib/hooks/useModalSurface";
import { useBodyScrollLock } from "@/lib/hooks/useBodyScrollLock";

type ShellProps = {
  /**
   * The media to page through — `mediaAttachments(...)`, images and videos in
   * display order. The attachment itself, not a url: the lightbox needs `kind`
   * to pick a renderer, `posterUrl` for a video's thumbnail and `renditions`
   * for an image's, and passing urls meant every caller flattened those away
   * and rebuilt a parallel array to get them back.
   *
   * `Ready<…>`: the lightbox displays a file, so it takes only attachments
   * there are bytes behind (OSO-490). Callers narrow with `readyAttachments`
   * — which is also what keeps the tile order and the lightbox order the same
   * list, the thing OSO-477 was.
   */
  items: Ready<Attachment>[];
  initialIndex: number;
  onClose: () => void;
};

/**
 * One cell of the thumbnail strip: the job's poster for a video, the thumb
 * rendition for an image — never the original, which is the whole point.
 *
 * A video's poster can legitimately be missing (uploaded before the transcode
 * job shipped, or still processing), and an `<img>` with `src={null}` is a
 * broken-image glyph. The play badge is the fallback, and it doubles as the
 * only thing that distinguishes a video cell from a photo at 56px.
 */
function LightboxThumb({ item }: { item: Ready<Attachment> }) {
  const url = item.kind === "video" ? item.posterUrl : (item.renditions?.thumb ?? item.url);

  return (
    <span className="relative block w-full h-full bg-muted">
      {url && <img src={url} alt="" className="w-full h-full object-cover" />}
      {item.kind === "video" && (
        <span className="absolute inset-0 flex items-center justify-center">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white" className="drop-shadow">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      )}
    </span>
  );
}

type PostPanelProps = {
  post: PostCard;
  currentReaction: string | null;
  /** The image for the viewer's own reaction, if it is a custom emoji. */
  currentReactionUrl: string | null;
  reactionCounts: Summary["counts"];
  onReact: (emoji: string, url?: string | null) => void;
  comments: ThreadComment[];
  replyCount: number;
  currentUser: CurrentUser | null;
  commentText: string;
  setCommentText: (text: string) => void;
  onSubmitComment: () => void;
  isSubmittingComment: boolean;
  commentAnonymous: boolean;
  setCommentAnonymous: (fn: (v: boolean) => boolean) => void;
  canSubmitComment: boolean;
  anonymousAllowed: boolean;
  /** The card's attachment state — the lightbox writes into the same comment. */
  commentFiles: ReturnType<typeof useAttachments>;
};

/**
 * Either supply your own sidebar node, or supply the post props and get the
 * default panel (post text, reactions, comments, comment box).
 */
type ImageLightboxProps =
  | (ShellProps & PostPanelProps & { sidebar?: undefined })
  | (ShellProps & {
      /** Replaces the default post panel. The strip of thumbnails is still
          appended below it, so this node should own the space above it
          (`lg:flex-1 lg:min-h-0`). */
      sidebar: ReactNode;
      post?: never;
    });

/**
 * Whether a keystroke belongs to something the reader is typing in, and so is
 * not a shortcut. Covers the form controls and the `contentEditable` composers
 * (`RichTextInput`) alike — `isContentEditable` is the browser's own answer
 * and is true for descendants of the editable host too; jsdom does not
 * implement it, which is what the DOM fallback is for.
 */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return (
    target.isContentEditable ||
    target.closest('[contenteditable]:not([contenteditable="false"])') !== null
  );
}

export function ImageLightbox(props: ImageLightboxProps) {
  const { items, initialIndex, onClose } = props;
  const [index, setIndex] = useState(initialIndex);
  const hasMultiple = items.length > 1;
  const current = items[index];

  const goPrev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : items.length - 1));
  }, [items.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i < items.length - 1 ? i + 1 : 0));
  }, [items.length]);

  /**
   * The lightbox is `z-[100]` — in front of every dialog and every chat window
   * — so while it is open it is the one that answers. A `Dialog` opened INSIDE
   * it (a confirmation from the comment thread in its sidebar) out-ranks it by
   * containment, which is why the panel is registered too. A SECOND lightbox,
   * opened from an image on a comment in that same sidebar, is a peer rather
   * than a nested layer — both portal to `document.body` — and registration
   * order settles it: the inner one registered later, so it answers.
   */
  const panelRef = useRef<HTMLDivElement>(null);
  const isTopmost = useEscapeLayer(true, ESCAPE_LAYER.lightbox, panelRef);

  /**
   * FOCUS ONLY — the keyboard logic below stays exactly where it is (OSO-639).
   *
   * This component had 494 lines and not one ARIA attribute. The overlay was a
   * plain `div`: no role, no name, nothing behind it hidden. Focus stayed on
   * the button that opened it, so all five tab stops measured were OUTSIDE the
   * overlay, in the page under the 70% black backdrop. Escape and the arrows
   * worked — they are carefully tuned against four other layers, a few lines
   * down — but after Escape, focus was not returned to the trigger.
   *
   * `useFocusContainment` rather than `useModalSurface`: the latter would
   * register this panel in the Escape stack a SECOND time, underneath the
   * registration above whose `isTopmost` those four guards read.
   */
  useFocusContainment({ open: true, panelRef, isTopmost });

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Escape only. Both guards used to sit above this branch and so gated
        // the arrow keys too, on the unscoped question "is anything at all
        // drawn over me" — which the arrows must not ask: the `z-[200]` profile
        // card, opened by resting the pointer on a commenter's name in this
        // lightbox's own sidebar, answered yes and image navigation went dead
        // over a card that ignores arrows entirely.
        //
        // What they do gate: a key something inside has already answered is not
        // ours. `useDismissable` marks the Escape that closed a dropdown as
        // handled from a capture-phase listener that always runs first, and
        // without that the same keystroke would tear down the lightbox the
        // dropdown was drawn over.
        if (e.defaultPrevented || !isTopmost()) return;
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        // "Not the unscoped question" is not "nobody else's". Skipping the
        // `"all"` scope says nothing about the four ways an arrow can already be
        // spoken for:
        //
        //   - something inside has answered it — same rule as Escape;
        //   - the reader is typing. This listener is on `document`, and this
        //     component's own sidebar renders a `RichTextInput` comment box;
        //     without the check, ArrowLeft to fix a typo mid-comment flipped to
        //     the previous image;
        //   - a lower-ranked surface of ours is open on us. `isEditableTarget`
        //     covers only the fields inside a `Dialog` opened from the sidebar,
        //     and a dialog autofocuses its first focusable, usually a
        //     `<button>` — so an arrow pressed at a "slet denne kommentar?"
        //     confirmation silently changed the image it was asked about.
        //     `hasLayerInside` is the containment half of the registry;
        //   - another lightbox is open over us, which containment cannot see.
        //     The sidebar renders `CommentThread` → `CommentItem`, and a
        //     comment's own image opens a second `ImageLightbox` — portaled to
        //     `document.body` like this one, so the inner panel is not a DOM
        //     descendant of the outer and `hasLayerInside` says no. Both
        //     handlers then acted on one ArrowRight, and closing the inner one
        //     left the outer showing an image the reader never navigated to.
        //     `isTopmost("rank")` is the peer question — among lightboxes only,
        //     am I the last registered? `"all"` would bring back the hover card.
        if (
          !hasMultiple ||
          e.defaultPrevented ||
          isEditableTarget(e.target) ||
          !isTopmost("rank") ||
          hasLayerInside(panelRef.current)
        )
          return;
        // Answered, so spent — the same invariant the Escape branch holds.
        e.preventDefault();
        if (e.key === "ArrowLeft") goPrev();
        else goNext();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, goPrev, goNext, hasMultiple, isTopmost]);

  // Counted, because this component can be open inside itself: a comment in
  // the sidebar opens a second lightbox, and a plain reset let the page behind
  // scroll the moment that inner one closed (OSO-473).
  useBodyScrollLock();

  const sidebar = props.post ? <PostPanel {...props} /> : props.sidebar;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
      tabIndex={-1}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 outline-none"
      onClick={onClose}
    >
      {/* Mobile: full-screen vertical | Desktop: 90% side-by-side */}
      <div
        className="flex flex-col lg:flex-row w-full h-full lg:w-[90%] lg:h-[90%] lg:rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
      <div className="lg:hidden flex items-center h-11 px-2 bg-surface border-b border-border shrink-0">
        <button
          type="button"
          aria-label="Luk billedvisning"
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted transition-colors"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        {hasMultiple && (
          <span className="ml-1 text-sm text-muted-foreground">
            {index + 1} / {items.length}
          </span>
        )}
      </div>

      {/* Image area. On mobile this sits above the post text in a column, so the
          image gets a viewport cap — a tall portrait photo would otherwise
          stretch the area until the post itself is off-screen (OSO-325). */}
      <div className="relative bg-black flex items-center justify-center min-h-[40vh] lg:flex-1 lg:min-w-0 shrink-0">
        <button
          type="button"
          aria-label="Luk billedvisning"
          onClick={onClose}
          className="hidden lg:flex absolute top-3 left-3 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white items-center justify-center transition-colors"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {hasMultiple && (
          <span className="hidden lg:block absolute top-4 left-16 text-white/80 text-sm font-medium">
            {index + 1} / {items.length}
          </span>
        )}

        {current.kind === "video" ? (
          /* KEYED BY ID so paging away from a playing clip unmounts the
             <video> rather than swapping its src underneath it — otherwise the
             audio of the one you just left keeps playing over the next. */
          <VideoPlayer
            key={current.id}
            src={current.url}
            hlsUrl={current.hlsUrl ?? undefined}
            filename={current.filename}
            poster={current.posterUrl ?? undefined}
            /* No height budget here — OSO-665's 480 px is a feed's rule, and
               the lightbox exists precisely to escape it. The classes below
               are the only thing that bounds this player. */
            maxHeight={null}
            className="max-w-full max-h-[55vh] lg:max-h-full"
          />
        ) : (
          <img
            src={current.url}
            alt=""
            className="max-w-full max-h-[55vh] lg:max-h-full object-contain select-none"
            draggable={false}
          />
        )}

        {hasMultiple && (
          <>
            <button
              type="button"
              aria-label="Forrige billede"
              onClick={goPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Næste billede"
              onClick={goNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </>
        )}

        {/* Thumbnail strip — mobile only (inside image area) */}
        {hasMultiple && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 lg:hidden">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Vis billede ${i + 1} af ${items.length}`}
                aria-current={i === index}
                onClick={() => setIndex(i)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === index ? "bg-white" : "bg-white/40"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sidebar — scrollable on mobile, fixed width on desktop. It clips, so
          a popover opened from a comment in here (the reaction picker) is
          placed against this box rather than the window — OSO-560. */}
      <div
        data-popover-bounds=""
        className="flex-1 lg:flex-none lg:w-96 bg-surface lg:border-l border-border flex flex-col min-h-0 overflow-y-auto lg:overflow-hidden"
      >
        {sidebar}

        {hasMultiple && (
          <div className="hidden lg:block px-4 py-3 border-t border-border shrink-0">
            <div className="flex gap-1.5 overflow-x-auto">
              {items.map((item, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Vis billede ${i + 1} af ${items.length}`}
                  aria-current={i === index}
                  onClick={() => setIndex(i)}
                  className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-colors ${
                    i === index
                      ? "border-indigo-500"
                      : "border-transparent hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
                >
                  <LightboxThumb item={item} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>,
    document.body
  );
}

/** The default sidebar: the post's text, reactions and comments. */
function PostPanel({
  post,
  currentReaction,
  currentReactionUrl,
  reactionCounts,
  onReact,
  comments,
  replyCount,
  currentUser,
  commentText,
  setCommentText,
  onSubmitComment,
  isSubmittingComment,
  commentAnonymous,
  setCommentAnonymous,
  canSubmitComment,
  anonymousAllowed,
  commentFiles,
}: PostPanelProps) {
  const commentInputRef = useRef<RichTextInputHandle>(null);
  const hasReactions = reactionCounts.some((c) => c.count > 0);


  const hasBody = post.bodyHtml
    ? post.bodyHtml.replace(/<[^>]*>/g, "").trim().length > 0
    : false;

  const showTitle = showPostTitle(post);

  return (
    <>
      {/* Everything above the comment box scrolls together. Pinning the post
          text instead clipped long posts: the text block was taller than the
          sidebar, and the overflow simply fell off the bottom (OSO-323). */}
      <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
        <div className="px-4 pt-4 pb-3 border-b border-border">
          <div className="flex items-center gap-2.5">
            <Avatar
              url={post.author?.avatar.medium ?? ""}
              name={post.author?.name ?? "Anonym"}
              size="sm"
            />
            <div>
              <p className="font-semibold text-sm text-foreground">
                {post.author?.name ?? "Anonym"}
              </p>
              <TimeAgo date={post.createdAt} />
            </div>
          </div>

          <div className="mt-3">
            {showTitle && (
              <h3 className="font-semibold text-foreground mb-1">{post.title}</h3>
            )}
            {hasBody ? (
              /* THROUGH RichContent, like every other cooked body. This was a
                 raw `dangerouslySetInnerHTML`, which is why it had to hand-apply
                 `theta-emoji-scale` — the rule RichContent already attaches for
                 every surface (OSO-450) — and why audio, YouTube and onebox
                 cards silently did not render here at all.

                 NO `images` PROP, and that is not an oversight: the server
                 strips a post's uploads out of `bodyHtml` and sends them as
                 attachments, so nothing is left to gather and the gallery stays
                 empty (`count > 0` guards it). The photos are the main pane
                 beside this text; rendering them again in the sidebar is the
                 one thing this must not do. */
              <RichContent
                html={post.bodyHtml}
                dismissedLinkPreviewUrl={post.dismissedLinkPreviewUrl}
                density="post"
                categoryId={post.group.id}
                className="text-muted-foreground text-sm leading-relaxed"
              />
            ) : (
              post.excerpt && (
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {post.excerpt}
                </p>
              )
            )}
          </div>
        </div>

        {(hasReactions || replyCount > 0) && (
          <div className="mx-4 py-2 flex items-center justify-between">
            {hasReactions ? <ReactionSummary counts={reactionCounts} /> : <span />}
            {replyCount > 0 && (
              <span className="text-sm text-muted-foreground">
                {replyCount} kommentar{replyCount !== 1 ? "er" : ""}
              </span>
            )}
          </div>
        )}

        <PostActionRow
          groupId={post.group.id}
          currentReaction={currentReaction}
          currentReactionUrl={currentReactionUrl}
          onReact={onReact}
          onComment={() => commentInputRef.current?.focus()}
        />

        <CommentThread
          comments={comments}
          postId={post.id}
          groupId={post.group.id}
          groupSlug={post.group.slug}
          currentUserId={currentUser?.id ?? null}
          fullThread
        />
      </div>

      {/* THE SHARED COMPOSER, not a copy of it. This panel used to carry its
          own transcription of `PostCommentForm`, which is exactly how it ended
          up the one comment box with no shortcut hint (OSO-532) and no send
          button (OSO-534): both were added to the original and the copy stayed
          behind. Everything that differed is a prop. */}
      {currentUser && (
        <PostCommentForm
          mentionRef={commentInputRef}
          currentUser={currentUser}
          commentAnonymous={commentAnonymous}
          setCommentAnonymous={setCommentAnonymous}
          commentText={commentText}
          setCommentText={setCommentText}
          groupId={post.group.id}
          groupSlug={post.group.slug}
          submitComment={onSubmitComment}
          commentFiles={commentFiles}
          isSubmitting={isSubmittingComment}
          canSubmitComment={canSubmitComment}
          anonymousAllowed={anonymousAllowed}
          // BLUR, NOT CLEAR: this box is part of the panel and cannot be
          // hidden, so stepping out of it is the equivalent of the cancel the
          // other composers pass — and a second Escape then closes the
          // lightbox, as it would from anywhere else.
          onEscape={() => commentInputRef.current?.blur()}
          className="shrink-0"
        />
      )}
    </>
  );
}
